import { Box, Snackbar, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StepsList from '../components/common/TaskTemplatesSection/StepsList';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import { DisableContext } from '../contexts/DisableContextProvider';
import { useCreateTaskTemplate, useGetTaskTemplateById, useUpdateTaskTemplate } from '../services/react-query/task_templates';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { TemplateField } from '../types/FieldCommon';
import { Step } from '../types/Step';
import { TaskTemplate } from '../types/TaskTemplate';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator } from '../utility/inputUtils';
import TagsSection from '../components/common/TagsSection/TagsSection';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import { useArchiveTaskTemplate } from '../services/react-query/task_templates/useArchiveTaskTemplate';
import { ROUTE_PATHS } from '../routes/Routes';
import { useDuplicateTaskTemplate } from '../services/react-query/task_templates/useDuplicateTaskTemplate';
import _ from 'lodash';
import AccordionSection from '../components/AccordionSection';
import DuplicateModal from '../components/DuplicateModal';
import useStateWithDirty from '../hooks/useStateWithDirty';

export default function TaskTemplateItem() {
  const { uuid } = useParams();
  const { state: taskTemplate, setState: setTaskTemplate, resetState: resetTaskTemplate, isDirty } = useStateWithDirty<TaskTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const navigate = useNavigate();

  const { mutate: archiveTaskTemplate } = useArchiveTaskTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.TASK_TEMPLATES}`),
  });

  const { data, isLoading } = useGetTaskTemplateById({ uuid });

  const { mutate: updateTaskTemplate, isPending: isUpdating } = useUpdateTaskTemplate({
    uuid,
    onSuccessCallback: () => setSaved(true),
  });
  const { mutate: createTaskTemplate, isPending: isCreating } = useCreateTaskTemplate({ onSuccessCallback: () => null });

  const { mutate: duplicateTemplateMutation } = useDuplicateTaskTemplate((newUuid) => {
    navigate(`/task-templates/${newUuid}`);
  });

  useEffect(() => {
    if (data) resetTaskTemplate(data);
  }, [data]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!taskTemplate?.discarded_at);

  if (isLoading || taskTemplate === null) return <Loading />;

  const handleSave = () => {
    if (uuid === 'new') {
      createTaskTemplate(taskTemplate);
    } else {
      updateTaskTemplate(taskTemplate);
    }
  };

  const setFields = (fields: TemplateField[]) => setTaskTemplate({ ...taskTemplate, fields });

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  return (
    <Box>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} Task Template`}
        onSave={handleSave}
        archived={!!taskTemplate.discarded_at}
        isLoading={isCreating || isUpdating}
        disableActions={!isDirty}
        showMoreOptions={
          uuid !== 'new' ? (
            <MoreOptions onArchive={archiveTaskTemplate} onDuplicate={() => setIsDuplicateModalOpen(true)} name={taskTemplate?.name} itemName="task template" />
          ) : null
        }
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              autoFocus
              onChange={(event) => setTaskTemplate(taskTemplate ? { ...taskTemplate, name: event.target.value } : null)}
              value={taskTemplate?.name ?? ''}
              disabled={disabled}
              {...errorPopulator(taskTemplate, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setTaskTemplate(taskTemplate ? { ...taskTemplate, description: event.target.value } : null)}
              value={taskTemplate?.description ?? ''}
              disabled={disabled}
              {...errorPopulator(taskTemplate, 'description')}
            />
          </Box>
        </Box>
      </AccordionSection>

      <StepsList steps={taskTemplate.steps || []} setSteps={(newSteps: Step[]) => setTaskTemplate({ ...taskTemplate, steps: newSteps })} />

      <TemplateFieldsSection fields={taskTemplate.fields} setFields={setFields} collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_FIELDS} />

      <TagsSection
        tags={taskTemplate.tags}
        collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_TAGS}
        setTags={(tags) => setTaskTemplate({ ...taskTemplate, tags })}
      />

      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(name) => handleDuplicate(name)}
        defaultName={`${taskTemplate?.name ?? ''} Copy`}
        itemName="task template"
      />
      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
